import {
  createSignal,
  For,
  Show,
  onCleanup,
  onMount,
  createEffect,
  createMemo,
} from "solid-js";
import { useParams, useSearchParams } from "@solidjs/router";
import { RoomHub } from "../Multidevice/RoomHub";
import { runAzureLiveTranscribe } from "../api/runAzureLiveTranscribe";
import { LanguageBadge } from "../components/LanguageBadge";
import { PanelHeader } from "../components/PanelHeader";
import { getSession } from "../api/sessions";
import { useTTS } from "../hooks/useTTS";

import { uiHash } from "../utils/telemetry";

type Role = "caseworker" | "respondent";
type Line = {
  id: string;
  speaker: 1 | 2;
  text?: string;
  translation?: string;
  translationError?: boolean;
  confidence?: number;
  lang?: string;
  ts?: number;
};

export default function GuestPage() {
  const params = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const sessionId = () => params.id as string;
  const role: Role = "respondent"; // this page is the respondent view

  const guestLang = () =>
    (Array.isArray(sp.lang) ? sp.lang[0] : sp.lang) || "en-US";

  const [status, setStatus] = createSignal<
    "idle" | "connecting" | "live" | "ended" | "error"
  >("idle");
  const [error, setError] = createSignal<string>("");
  const [showPause, setShowPause] = createSignal(false);
  const [showEnded, setShowEnded] = createSignal(false);
  const [micOn, setMicOn] = createSignal(false);

  const [leftLines, setLeftLines] = createSignal<Line[]>([]);
  const [rightLines, setRightLines] = createSignal<Line[]>([]);
  const [interpEnabled, setInterpEnabled] = createSignal(false);

  let leftBox!: HTMLDivElement;
  let rightBox!: HTMLDivElement;
  let mergedBox!: HTMLDivElement;
  const [leftPinned, setLeftPinned] = createSignal(false);
  const [rightPinned, setRightPinned] = createSignal(false);
  const [mergedPinned, setMergedPinned] = createSignal(false);
  const onScroll = (box: HTMLDivElement, setPinned: (v: boolean) => void) => {
    const atBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 4;
    setPinned(!atBottom);
  };

  const hub = new RoomHub();
  let stopMic: null | (() => Promise<void>) = null;
  let ttsSuppressUntil = 0;

  // Text-to-Speech (Web Speech API)
  const tts = useTTS({
    provider: "webspeech",
    storageKey: `tts-guest-${sessionId()}`,
    defaultLang: guestLang(),
  });
  createEffect(() => {
    // Keep TTS language synced to guest language param
    tts.setLang(guestLang());
  });

  function upsert(
    list: Line[],
    evt: Partial<Line> & { id: string; speaker: 1 | 2 }
  ) {
    const idx = list.findIndex((l) => l.id === evt.id);
    const base: Line =
      idx >= 0
        ? { ...list[idx] }
        : ({ id: evt.id, speaker: evt.speaker } as Line);
    if (evt.text !== undefined) base.text = evt.text;
    if (evt.translation !== undefined) base.translation = evt.translation;
    if (evt.translationError) base.translationError = true;
    if (evt.confidence !== undefined) base.confidence = evt.confidence;
    if (evt.lang !== undefined) base.lang = evt.lang;
    const next = [...list];
    if (idx >= 0) next[idx] = base;
    else next.push(base);
    return next;
  }

  async function translate(text: string, to: string): Promise<string | null> {
    try {
      const mod = await import("../api/translateBatch");
      const [t] = await mod.translateBatch([text], to);
      return t || text;
    } catch {
      return null;
    }
  }

  async function startMic() {
    if (stopMic) return;
    if (status() === "ended") return;
    try {
      const langs = [guestLang()];
      stopMic = await runAzureLiveTranscribe({
        langs,
        onUtterance: (u) => {
          // Soft pause: ignore local mic while TTS is speaking
          try {
            if (tts.speaking && tts.speaking()) return;
          } catch {}
          // Fallback suppression window right after manual play
          if (Date.now() < ttsSuppressUntil) return;
          // Render immediately on right panel as S2 originals
          const tsMs = Math.round(u.start ? u.start * 1000 : Date.now());
          const id = `${tsMs}-${(u.text || "").slice(0, 32)}`;
          setRightLines((prev) =>
            upsert(prev, {
              id,
              speaker: 2,
              text: u.text,
              lang: u.lang,
              confidence: u.confidence,
              ts: tsMs,
            })
          );
          // Broadcast to host via hub
          hub
            .sendGuestUtter({
              sessionId: sessionId(),
              text: u.text || "",
              lang: u.lang,
              confidence: u.confidence,
              ts: tsMs,
              id,
              from: "guest",
            })
            .catch(() => {});
        },
        onComplete: () => {},
        onError: (e) => setError(e.message || String(e)),
      });
      await hub.sendGuestPresence({ sessionId: sessionId(), state: "mic_on" });
      setMicOn(true);
    } catch (e: any) {
      setError(e?.message || String(e));
    }
  }

  async function stopMicSafe() {
    try {
      await stopMic?.();
    } catch {}
    stopMic = null;
    try {
      await hub.sendGuestPresence({ sessionId: sessionId(), state: "mic_off" });
    } catch {}
    setMicOn(false);
  }

  onMount(async () => {
    try {
      setStatus("connecting");
      // Check server truth for session status so refresh reflects "ended"
      try {
        const s = await getSession(sessionId());
        if ((s?.status as any) === "completed") {
          setShowEnded(true);
          setStatus("ended");
        }
        const meta: any = s?.metadata || {};
        setInterpEnabled(Boolean(meta.interpreterMode));
      } catch {}
      await hub.connect();
      await hub.join(sessionId());
      await hub.sendGuestPresence({ sessionId: sessionId(), state: "joined" });

      // Host utterances
      hub.onHostUtter(async (h) => {
        if (h.sessionId !== sessionId()) return;
        const id =
          h.id || (h.ts ? `host-${h.ts}` : `host-${uiHash(h.text || "")}`);
        if (interpEnabled()) {
          // Interpreter on → left panel shows S1→S2 translation
          let translated: string | null = null;
          const to = guestLang();
          const hostLangShort = String(h.lang || "").split("-")[0];
          const guestLangShort = String(to || "").split("-")[0];
          if (to && (!h.lang || hostLangShort !== guestLangShort)) {
            translated = await translate(h.text || "", to);
          }
          setLeftLines((prev) =>
            upsert(prev, {
              id,
              speaker: 1,
              text: h.text,
              translation:
                translated === null ? undefined : translated || undefined,
              translationError: translated === null,
              confidence: h.confidence,
              lang: h.lang,
              ts: h.ts || Date.now(),
            })
          );
        } else {
          // Interpreter off → route host lines to the single panel (right)
          setRightLines((prev) =>
            upsert(prev, {
              id,
              speaker: 1,
              text: h.text,
              lang: h.lang,
              confidence: h.confidence,
              ts: h.ts || Date.now(),
            })
          );
        }
      });

      // Guest echo (if needed) → right panel already handled locally on mic
      hub.onGuestUtter((g) => {
        if (g.sessionId !== sessionId()) return;
        const id =
          g.id || (g.ts ? `guest-${g.ts}` : `guest-${uiHash(g.text || "")}`);
        setRightLines((prev) =>
          upsert(prev, {
            id,
            speaker: 2,
            text: g.text,
            lang: g.lang,
            confidence: g.confidence,
            ts: g.ts || Date.now(),
          })
        );
      });

      // Host status → show popups and stop mic on ended
      hub.onHostStatus((s) => {
        if (s.sessionId !== sessionId()) return;
        if (s.status === "paused") {
          setShowPause(true);
          setTimeout(() => setShowPause(false), 2500);
        }
        if (s.status === "ended") {
          setShowEnded(true);
          stopMicSafe();
          setStatus("ended");
        }
      });

      // Only flip to live if we are not already ended
      if (!showEnded()) setStatus("live");
    } catch (e: any) {
      setError(e?.message || String(e));
      setStatus("error");
    }
  });

  onCleanup(async () => {
    await stopMicSafe();
    try {
      await hub.disconnect();
    } catch {}
  });

  const langName = (tag: string) => {
    try {
      if (!tag) return "Unknown";
      const dn = new Intl.DisplayNames("en", { type: "language" });
      return dn.of(tag) || tag;
    } catch {
      return tag || "Unknown";
    }
  };

  const Panel = (p: {
    title: string;
    badge: string;
    lines: Line[];
    refSetter?: (el: HTMLDivElement) => void;
    onScroll?: (e: UIEvent) => void;
    showCopyAll?: boolean;
    preferTranslationForSpeaker1?: boolean;
  }) => (
    <div class="card">
      <div class="flex items-center gap-2 mb-2">
        <PanelHeader title={p.title} />
        <LanguageBadge label={p.badge} />
        <Show when={p.showCopyAll}>
          <button
            onClick={() => {
              const text = p.lines
                .map(
                  (u) =>
                    `[S${u.speaker}] ${u.text || ""}${
                      u.translation ? `  ⇢  ${u.translation}` : ""
                    }`
                )
                .join("\n");
              navigator.clipboard.writeText(text);
            }}
            title="Copy all"
            class="text-gray-500 hover:text-gray-700 ml-auto"
          >
            📋
          </button>
        </Show>
      </div>

      <div
        ref={p.refSetter}
        onScroll={p.onScroll as any}
        class="space-y-3 max-h-[70vh] overflow-auto"
      >
        <For each={p.lines}>
          {(u) => (
            <p
              class={`max-w-[80%] rounded-xl px-3 py-1 ${
                u.speaker % 2
                  ? "mr-auto bg-indigo-100 border border-indigo-500 text-indigo-900"
                  : "ml-auto bg-blue-50 border border-blue-400 text-blue-900"
              }`}
            >
              <span class="text-xs opacity-70 mr-1">
                {u.speaker === 1 ? "Speaker 1" : "Speaker 2"}
              </span>
              <span>
                {p.preferTranslationForSpeaker1 && u.speaker === 1
                  ? u.translation || u.text
                  : u.text}
              </span>
              <Show when={u.speaker === 1}>
                <button
                  class="ml-2 text-xs text-gray-600 hover:text-gray-800 align-middle"
                  title="Play this message"
                  aria-label="Play this message"
                  onClick={() => {
                    const txt =
                      (p.preferTranslationForSpeaker1 && u.speaker === 1
                        ? u.translation || u.text
                        : u.text) || "";
                    const speakLang =
                      p.preferTranslationForSpeaker1 && u.speaker === 1
                        ? guestLang()
                        : "en-US";
                    if (!txt.trim()) return;
                    tts.stop();
                    tts.speakNow({ text: txt, lang: speakLang });
                    const words = txt.trim().split(/\s+/).length;
                    const durationMs = Math.min(
                      12000,
                      Math.max(1500, words * 350)
                    );
                    ttsSuppressUntil = Date.now() + durationMs;
                  }}
                >
                  🔊
                </button>
              </Show>
              <For
                each={
                  p.preferTranslationForSpeaker1 && u.speaker === 1
                    ? []
                    : u.translation
                    ? [u.translation]
                    : []
                }
              >
                {(t) => (
                  <span class="block italic text-slate-600 text-xs mt-0.5">
                    {t}
                  </span>
                )}
              </For>
              <For each={u.translationError ? [true] : []}>
                {() => (
                  <span class="ml-2 text-[10px] px-1 py-[1px] rounded bg-rose-50 text-rose-700 border border-rose-200">
                    translation unavailable
                  </span>
                )}
              </For>
            </p>
          )}
        </For>
        <For each={p.lines.length ? [] : [true]}>
          {() => (
            <p class="text-gray-500 text-sm">
              {status() === "idle"
                ? "Waiting to connect…"
                : status() === "live"
                ? ""
                : status() === "error"
                ? error()
                : "Connecting…"}
            </p>
          )}
        </For>
      </div>
    </div>
  );

  const onToggleMic = async () => {
    if (status() === "ended") return;
    if (stopMic) await stopMicSafe();
    else await startMic();
  };

  // On new lines, auto-scroll the appropriate panel
  createEffect(() => {
    leftLines().length;
    queueMicrotask(() => {
      if (!leftPinned() && leftBox) leftBox.scrollTop = leftBox.scrollHeight;
    });
  });
  createEffect(() => {
    rightLines().length;
    queueMicrotask(() => {
      if (!rightPinned() && rightBox)
        rightBox.scrollTop = rightBox.scrollHeight;
    });
  });
  // Single merged panel auto-scroll
  const mergedLines = createMemo<Line[]>(() => {
    const all = [...leftLines(), ...rightLines()];
    all.sort((a, b) => (a.ts || 0) - (b.ts || 0));
    return all;
  });
  createEffect(() => {
    mergedLines().length;
    queueMicrotask(() => {
      if (!mergedPinned() && mergedBox)
        mergedBox.scrollTop = mergedBox.scrollHeight;
    });
  });

  // Auto-speak removed: manual play only

  return (
    <div class="min-h-screen p-6 overflow-y-auto">
      <div class="max-w-4xl mx-auto space-y-4">
        <header class="flex items-center justify-between">
          <div>
            <h1 class="text-xl font-semibold">Respondent View</h1>
            <p class="text-sm text-gray-600">
              Session: <strong>{sessionId()}</strong>
              <br />
              Language: <strong>{guestLang()}</strong>
            </p>
          </div>
          <div class="flex items-center gap-3">
            <span
              class={`text-xs px-2 py-[2px] rounded border ${
                status() === "live"
                  ? "bg-green-50 text-green-700 border-green-200"
                  : status() === "ended"
                  ? "bg-gray-50 text-gray-600 border-gray-200"
                  : status() === "error"
                  ? "bg-rose-50 text-rose-700 border-rose-200"
                  : "bg-yellow-50 text-yellow-700 border-yellow-200"
              }`}
            >
              {status() === "live"
                ? "Live"
                : status() === "ended"
                ? "Session ended"
                : status() === "error"
                ? "Error"
                : "Connecting"}
            </span>
            <Show when={micOn() && status() !== "ended"}>
              <span
                class="inline-block w-2 h-2 rounded-full bg-green-500"
                title="Mic is on"
                aria-label="Mic is on"
              />
            </Show>
            <button
              class={`px-3 py-1 rounded text-sm transition-colors ${
                micOn()
                  ? "bg-green-600 text-white border border-green-600 hover:bg-green-700"
                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              } ${showEnded() ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={onToggleMic}
              disabled={showEnded()}
              aria-pressed={micOn()}
              aria-label={
                micOn() ? "Turn microphone off" : "Turn microphone on"
              }
              title={
                micOn()
                  ? "Mic is ON — click to turn off"
                  : "Mic is OFF — click to turn on"
              }
            >
              {micOn() ? "🎤 Mic On" : "🎤 Mic Off"}
            </button>
          </div>
        </header>

        <Show when={showPause()}>
          <div class="rounded border p-3 bg-yellow-50 border-yellow-200 text-yellow-900">
            The host has paused the session.
          </div>
        </Show>
        <Show when={showEnded()}>
          <div class="rounded border p-3 bg-rose-50 border-rose-200 text-rose-900">
            The session has ended.
          </div>
        </Show>

        <main class="grid grid-cols-1 gap-4">
          <Panel
            title="Transcript"
            badge={`${langName(guestLang())}`}
            lines={interpEnabled() ? mergedLines() : rightLines()}
            refSetter={(el) => (mergedBox = el)}
            onScroll={() => onScroll(mergedBox, setMergedPinned)}
            preferTranslationForSpeaker1={interpEnabled()}
          />
        </main>
      </div>
    </div>
  );
}
